import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Plus, Trash2, Upload, Download } from "lucide-react";
import { format, parse, isValid } from "date-fns";

interface MeetingEntry {
  id?: string;
  meeting_date: string;
  topic: string;
  notes: string | null;
}

const MeetingLogEditor = () => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [entries, setEntries] = useState<MeetingEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [newEntry, setNewEntry] = useState<MeetingEntry>({
    meeting_date: format(new Date(), 'yyyy-MM-dd'),
    topic: "",
    notes: null,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data, error } = await supabase
        .from('presentation_meeting_log')
        .select('*')
        .order('meeting_date', { ascending: false });

      if (error) throw error;
      setEntries(data || []);
    } catch (error) {
      console.error('Error loading meeting log:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addEntry = async () => {
    if (!newEntry.topic.trim()) {
      toast({ title: "Validation", description: "Topic is required.", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('presentation_meeting_log')
        .insert({
          meeting_date: newEntry.meeting_date,
          topic: newEntry.topic.trim(),
          notes: newEntry.notes?.trim() || null,
        });

      if (error) throw error;

      toast({ title: "Added", description: "Meeting log entry added." });
      setNewEntry({
        meeting_date: format(new Date(), 'yyyy-MM-dd'),
        topic: "",
        notes: null,
      });
      await loadData();
    } catch (error) {
      console.error('Insert error:', error);
      toast({ title: "Error", description: "Failed to add meeting entry.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const removeEntry = async (id: string) => {
    try {
      const { error } = await supabase
        .from('presentation_meeting_log')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setEntries(entries.filter(e => e.id !== id));
      toast({ title: "Deleted", description: "Meeting entry removed." });
    } catch (error) {
      console.error('Delete error:', error);
      toast({ title: "Error", description: "Failed to delete entry.", variant: "destructive" });
    }
  };

  const parseDateString = (dateStr: string): string | null => {
    // Try dd/MM/yyyy format first
    const parsed1 = parse(dateStr.trim(), 'dd/MM/yyyy', new Date());
    if (isValid(parsed1)) return format(parsed1, 'yyyy-MM-dd');

    // Try yyyy-MM-dd
    const parsed2 = parse(dateStr.trim(), 'yyyy-MM-dd', new Date());
    if (isValid(parsed2)) return format(parsed2, 'yyyy-MM-dd');

    // Try MM/dd/yyyy
    const parsed3 = parse(dateStr.trim(), 'MM/dd/yyyy', new Date());
    if (isValid(parsed3)) return format(parsed3, 'yyyy-MM-dd');

    return null;
  };

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);

      if (lines.length < 2) {
        toast({ title: "Error", description: "CSV must have a header row and at least one data row.", variant: "destructive" });
        return;
      }

      // Parse header to find column indices
      const header = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
      const dateIdx = header.findIndex(h => h.includes('date'));
      const topicIdx = header.findIndex(h => h.includes('topic') || h.includes('meeting') || h.includes('subject') || h.includes('name'));
      const notesIdx = header.findIndex(h => h.includes('note'));

      if (dateIdx === -1 || topicIdx === -1) {
        toast({ title: "Error", description: "CSV must have 'date' and 'topic' columns.", variant: "destructive" });
        return;
      }

      const rows: { meeting_date: string; topic: string; notes: string | null }[] = [];
      const errors: string[] = [];

      for (let i = 1; i < lines.length; i++) {
        // Simple CSV parsing (handles quoted fields)
        const cols = lines[i].match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g)?.map(c => c.trim().replace(/^"|"$/g, '')) || [];
        
        const dateStr = cols[dateIdx];
        const topic = cols[topicIdx];

        if (!dateStr || !topic) {
          errors.push(`Row ${i + 1}: Missing date or topic`);
          continue;
        }

        const parsedDate = parseDateString(dateStr);
        if (!parsedDate) {
          errors.push(`Row ${i + 1}: Invalid date "${dateStr}"`);
          continue;
        }

        rows.push({
          meeting_date: parsedDate,
          topic: topic.trim(),
          notes: notesIdx !== -1 && cols[notesIdx] ? cols[notesIdx].trim() : null,
        });
      }

      if (rows.length === 0) {
        toast({ title: "Error", description: `No valid rows found. ${errors.join('; ')}`, variant: "destructive" });
        return;
      }

      // Insert all valid rows
      const { error } = await supabase
        .from('presentation_meeting_log')
        .insert(rows);

      if (error) throw error;

      const message = errors.length > 0 
        ? `${rows.length} entries added. ${errors.length} rows skipped.`
        : `${rows.length} entries added successfully.`;

      toast({ title: "CSV Uploaded", description: message });
      await loadData();
    } catch (error) {
      console.error('CSV upload error:', error);
      toast({ title: "Error", description: "Failed to process CSV file.", variant: "destructive" });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const csv = 'date,topic,notes\n25/11/2025,Example Meeting Topic,Optional notes here\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'meeting_log_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Meeting Log Editor</CardTitle>
        <CardDescription>Manage the meeting/webinar log for the Adviser-Client Presentation</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* CSV Upload Section */}
        <div className="flex items-center gap-2 p-3 rounded-lg border border-dashed">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleCsvUpload}
            className="hidden"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
            Upload CSV
          </Button>
          <Button variant="ghost" size="sm" onClick={downloadTemplate}>
            <Download className="h-4 w-4 mr-1" />
            Template
          </Button>
          <span className="text-xs text-muted-foreground">CSV with date, topic, notes (optional) columns</span>
        </div>

        {/* Manual Entry */}
        <div className="flex gap-2 items-end">
          <div className="space-y-1">
            <label className="text-sm font-medium">Date</label>
            <Input
              type="date"
              value={newEntry.meeting_date}
              onChange={(e) => setNewEntry({ ...newEntry, meeting_date: e.target.value })}
              className="w-40"
            />
          </div>
          <div className="flex-1 space-y-1">
            <label className="text-sm font-medium">Topic</label>
            <Input
              value={newEntry.topic}
              onChange={(e) => setNewEntry({ ...newEntry, topic: e.target.value })}
              placeholder="Meeting topic..."
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Notes (optional)</label>
            <Input
              value={newEntry.notes || ""}
              onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
              placeholder="Brief notes..."
              className="w-48"
            />
          </div>
          <Button onClick={addEntry} disabled={isSaving} size="sm">
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
            Add
          </Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">Date</TableHead>
              <TableHead>Topic</TableHead>
              <TableHead className="w-48">Notes</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>{format(new Date(entry.meeting_date), 'dd/MM/yyyy')}</TableCell>
                <TableCell>{entry.topic}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{entry.notes || "—"}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => entry.id && removeEntry(entry.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {entries.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                  No meeting entries yet. Add manually or upload a CSV.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default MeetingLogEditor;
